 import {
  addSeconds,
  intervalToDuration,
  format,
  formatDuration,
  formatDistanceStrict,
  startOfDay,
  addHours,
  addMinutes,
  parseISO,
  isValid,
  isDate as isDateFns,
  parse,
} from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { isString, isDate } from 'lodash';
import { i18n } from '@/plugins/vue-i18n';

function validateLazyDate(val) {
  if (val != undefined) {
    if (typeof val === 'string' || val instanceof String) {
      val = parseISO(val);
    }
    return isValid(val);
  } else {
    // Allow async or unset data by returning true if undefined/null.
    return true;
  }
}

function secondsToEndTime(seconds) {
  return addSeconds(new Date(0), seconds);
}

function secondsToTime(seconds = 0) {
  return format(secondsToDatetime(seconds), 'HH:mm:ss');
}

function secondsToDatetime(seconds = 0) {
  // Gives time to offset from day start (0 seconds)
  // timezone independent
  const endTime = secondsToEndTime(seconds);
  const timezoneOffsetSeconds = endTime.getTimezoneOffset() * 60;
  return addSeconds(endTime, timezoneOffsetSeconds);
}


function secondsToDuration(seconds = 0, useAbbreviation = this.$vuetify.breakpoint.xsOnly) {
  const endTime = secondsToEndTime(seconds);
  const duration = intervalToDuration({
    start: new Date(0),
    end: endTime,
  });

  if (useAbbreviation) {
    const formattedDuration = formatDuration(duration, {
      locale: this.$root.dateLocale?.locale
    });
    const units = formattedDuration.split(', ').map(unit => {
      const [value, label] = unit.split(' ');
      const translationKey = `general.abbreviations.time.${label.toLowerCase()}`;
      const timeLabel = i18n.te(translationKey) ? i18n.t(translationKey) : label;
      return parseInt(value) !== 0 ? `${value} ${timeLabel}` : null;
    });
    const filteredUnits = units.filter(unit => unit !== null);
    return filteredUnits.join(', ');
  } else {
    return formatDuration(duration, {
      locale: this.$root.dateLocale?.locale,
      format: ['years', 'months', 'weeks', 'days', 'hours', 'minutes', 'seconds'],
      delimiter: ', ',
    });
  }
}

function setTimeToDateTime(dateTime, timeString) {
  const time = timeString.split(':');
  const hours = parseInt(time[0]);
  const minutes = parseInt(time[1]);
  const dayStart = startOfDay(dateTime);
  return addHours(addMinutes(dayStart, minutes), hours);
}

function secondsToHours(seconds = 0) {
  const startDate = new Date(0);
  const endDate = addSeconds(new Date(0), seconds);
  return formatDistanceStrict(startDate, endDate, {
    unit: 'hour',
    locale: this.$root.dateLocale?.locale,
  });
}

function dateToString(date) {
  // prefer date-fns formatISO
  return isDate(date) ? date?.toLocalISOString() : date;
}

function valToDatetime(val) {
  // return a value as datetime object
  // for example: '2021-11-24' or a Date object
  if (!val) return;
  if (isString(val)) {
    try {
      return parseISO(val);
    } catch (_e) {
      return;
    }
  } else if (isDate(val)) {
    return val;
  }
  return;
}

function timezoneOffsetHoursString(timezone) {
  return formatInTimeZone(new Date(), timezone, 'O');
}
function safeParseISO(val) {
  // If the val is already a valid Date object, return it
  if (isDateFns(val) && isValid(val)) {
    return val;
  }

  // Return undefined for empty vals
  if (!val) {
    return undefined;
  }

  let parsed;

  if (typeof val === 'string') {
    // Check for common date patterns before attempting conversion
    const hasDatePattern = /^\d{2}[-/]\d{2}[-/]\d{4}$|^\d{4}[-/]\d{2}[-/]\d{2}$|^\d{4}-\d{2}-\d{2}T|^\d{2}-\d{2}-\d{4}/.test(val);
    if (hasDatePattern) {
      // Attempt to parse as ISO string using date-fns parseISO
      parsed = parseISO(val);
      // If parsing fails, automatically try custom format
      if (!isValid(parsed)) {
        // Try assuming the format is dd-MM-yyyy
        parsed = parse(val, 'dd-MM-yyyy', new Date());
        // If that fails then try alternative common format (e.g. MM-dd-yyyy)
        if (!isValid(parsed)) {
          parsed = parse(val, 'MM-dd-yyyy', new Date());
        }
      }
    } else {
      return undefined;
    }
  } else if (typeof val === 'number') {
    // Only convert numbers that look like valid timestamps
    if (val > 946684800000) { // From 2000-01-01
      parsed = new Date(val);
    } else {
      return undefined;
    }
  } else {
    return undefined;
  }

  // Check if parsed date is valid and logical
  if (isValid(parsed) && parsed.getFullYear() >= 2000) {
    return parsed;
  }

  return undefined;
}

export {
  validateLazyDate,
  secondsToTime,
  secondsToDatetime,
  secondsToDuration,
  secondsToHours,
  dateToString,
  setTimeToDateTime,
  valToDatetime,
  timezoneOffsetHoursString,
  safeParseISO,
};
